import React, { useRef, useState, useEffect, forwardRef } from "react"
import cx from "classnames"
import { FormElement } from "./form"
import { withFieldLabelAndHint } from "./field"

type Props = {
  maxLength?: number
  className?: string
  inputClassName?: string
  autosize?: boolean
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void
} & FormElement &
  React.TextareaHTMLAttributes<HTMLTextAreaElement>

const TextArea = forwardRef<HTMLTextAreaElement, Props>((props, ref) => {
  const {
    autosize,
    className,
    inputClassName,
    status,
    maxLength,
    onChange,
    ...nativeProps
  } = props

  const internalRef = useRef<HTMLTextAreaElement>(null)
  const [length, setLength] = useState(0)
  const [height, setHeight] = useState(0)

  const hasAutosize = autosize || (maxLength && maxLength > 0)

  useEffect(() => {
    if (ref && "current" in ref) {
      ref.current = internalRef.current
    }
  }, [ref])

  const handleAutosize = (el?: HTMLTextAreaElement | null) => {
    if (!el) return
    if (hasAutosize) {
      setLength(el.value.length)
      setHeight(el.scrollHeight + 2)
    }
  }

  useEffect(() => {
    handleAutosize(internalRef.current)
  }, [autosize, maxLength])

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    handleAutosize(event.target)
    onChange && onChange(event)
  }

  return (
    <div
      className={cx("cobalt-TextAreaField", className, {
        "cobalt-TextAreaField--withLimit": maxLength && maxLength > 0,
        "cobalt-TextAreaField--success": status === "success",
        "cobalt-TextAreaField--error": status === "error",
      })}
    >
      <textarea
        {...nativeProps}
        maxLength={maxLength}
        style={
          hasAutosize
            ? {
                height: height + "px",
              }
            : {}
        }
        onChange={handleChange}
        className={cx("cobalt-TextAreaField__Input", inputClassName)}
        ref={internalRef}
      />
      {length > 0 && maxLength && maxLength > 0 && (
        <div className="cobalt-TextAreaField__RemainingChars">
          {maxLength - length} remaining characters
        </div>
      )}
    </div>
  )
})

TextArea.displayName = "TextArea"

const wrappedComponent = withFieldLabelAndHint(TextArea)
wrappedComponent.displayName = "TextArea"

export { wrappedComponent as TextArea }
